import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Tag, Row, Col } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import Loader from 'components/loader';
import { useI18n } from 'hooks/useI18n';
import { translateWorkoutGender, translateWorkoutGoal, translateWorkoutLevel } from 'helpers/workoutI18n';

import './template-preview.scss';

const LEVEL_CLASS_MAP = {
	Beginner: 'template-preview__tag--beginner',
	Intermediate: 'template-preview__tag--intermediate',
	Advanced: 'template-preview__tag--advanced',
};

const GOAL_CLASS_MAP = {
	Strength: 'template-preview__tag--strength',
	'Fat Loss': 'template-preview__tag--fat-loss',
	Endurance: 'template-preview__tag--endurance',
	Flexibility: 'template-preview__tag--flexibility',
	'Muscle Gain': 'template-preview__tag--muscle-gain',
};

const GENDER_CLASS_MAP = {
	Male: 'template-preview__tag--male',
	Female: 'template-preview__tag--female',
	Unisex: 'template-preview__tag--unisex',
};

const getWorkoutTemplateEntity = (payload) => {
	if (!payload) return null;
	if (Array.isArray(payload?.data)) return payload.data[0] || null;
	if (payload?.data && !Array.isArray(payload.data)) return payload.data;
	if (Array.isArray(payload)) return payload[0] || null;
	return payload;
};

const TemplatePreview = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutTemplateRedux = useSelector((state) => state.workoutTemplateRedux);

	useEffect(() => {
		dispatch(getApi('GET_WORKOUT_TEMPLATE_BY_ID', `${SERVER_IP}api/workoutTemplate/${id}`));
	}, [id, dispatch]);

	const template = getWorkoutTemplateEntity(workoutTemplateRedux?.currentTemplate);

	const loading = globalRedux.apiStatus.GET_WORKOUT_TEMPLATE_BY_ID === 'PENDING';
	if (loading) return <Loader />;
	if (!template) {
		return <div className="template-preview-page__empty">{t('workouts.template.preview.template_not_found')}</div>;
	}

	const days = Array.isArray(template.days) ? template.days : [];
	const activeDays = days.filter((day) => !day?.isRestDay).length;
	const restDays = days.filter((day) => day?.isRestDay).length;
	const totalExercises = days.reduce((count, day) => count + (day?.items?.length || 0), 0);

	return (
		<>
			<div className="template-preview-page">
				<div className="template-preview-page__shell">
					<div className="template-preview-hero">
						<div className="template-preview-hero__head">
							<Button className="template-preview-hero__back-btn" icon={<ArrowLeftOutlined />} onClick={() => navigate('/workout-templates')}>
								{t('workouts.template.preview.back_to_templates')}
							</Button>
							<div className="template-preview-hero__actions">
								<Button className="template-preview-hero__edit-btn" icon={<EditOutlined />} onClick={() => navigate(`/workout-templates/edit/${id}`)}>
									{t('workouts.template.list.edit_template')}
								</Button>
							</div>
						</div>
						<div className="template-preview-hero__body">
							<div className="template-preview-hero__copy">
								<span className="template-preview-hero__eyebrow">{t('workouts.template.preview.eyebrow')}</span>
								<h1 className="template-preview-hero__title">{template.name}</h1>
								<p className="template-preview-hero__subtitle">{t('workouts.template.preview.subtitle')}</p>
								<div className="template-preview-hero__tags">
									<Tag className={`template-preview__tag ${GENDER_CLASS_MAP[template.gender] || 'template-preview__tag--neutral'}`}>{translateWorkoutGender(t, template.gender, t('workouts.enums.gender.all_members'))}</Tag>
									<Tag className={`template-preview__tag ${LEVEL_CLASS_MAP[template.level] || 'template-preview__tag--neutral'}`}>{translateWorkoutLevel(t, template.level, t('workouts.enums.level.general'))}</Tag>
									<Tag className={`template-preview__tag ${GOAL_CLASS_MAP[template.goal] || 'template-preview__tag--neutral'}`}>{translateWorkoutGoal(t, template.goal, t('workouts.enums.goal.general_goal'))}</Tag>
								</div>
							</div>
							<div className="template-preview-hero__stats">
								<div className="template-preview-hero__stat-card">
									<span className="template-preview-hero__stat-value">{template.durationDays || days.length || 0}</span>
									<span className="template-preview-hero__stat-label">{t('workouts.template.preview.duration_days')}</span>
								</div>
								<div className="template-preview-hero__stat-card">
									<span className="template-preview-hero__stat-value">{activeDays}</span>
									<span className="template-preview-hero__stat-label">{t('workouts.template.preview.training_days')}</span>
								</div>
								<div className="template-preview-hero__stat-card">
									<span className="template-preview-hero__stat-value">{restDays}</span>
									<span className="template-preview-hero__stat-label">{t('workouts.template.preview.recovery_days')}</span>
								</div>
								<div className="template-preview-hero__stat-card">
									<span className="template-preview-hero__stat-value">{totalExercises}</span>
									<span className="template-preview-hero__stat-label">{t('workouts.template.preview.exercises_planned')}</span>
								</div>
							</div>
						</div>
					</div>

					<div className="template-preview-section">
						<div className="template-preview-section__header">
							<div>
								<h2 className="template-preview-section__title">{t('workouts.template.preview.program_schedule')}</h2>
								<p className="template-preview-section__subtitle">{t('workouts.template.preview.program_schedule_subtitle')}</p>
							</div>
							<div className="template-preview-section__summary">{days.length === 1 ? t('workouts.template.preview.scheduled_day_one', { count: days.length }) : t('workouts.template.preview.scheduled_day_other', { count: days.length })}</div>
						</div>
						<Row gutter={[16, 16]}>
							{days.map((day) => {
						const customTitle =
							day.title &&
							day.title !== `Day ${day.dayNumber}` &&
							day.title !== 'Rest'
								? day.title
								: null;
						return (
							<Col key={day.dayNumber} xs={24} sm={12} xl={8}>
								<Card
									className={`template-preview-day-card${day.isRestDay ? ' template-preview-day-card--rest' : ''}`}
									bordered={false}
								>
									<div className="template-preview-day-card__head">
										<div>
											<div className="template-preview-day-card__day">{t('workouts.common.day_label', { number: day.dayNumber })}</div>
											{customTitle && <div className="template-preview-day-card__title">{customTitle}</div>}
										</div>
										<div className={`template-preview-day-card__badge${day.isRestDay ? ' template-preview-day-card__badge--rest' : ''}`}>
											{day.isRestDay ? t('workouts.template.preview.rest_day') : (day.items?.length === 1 ? t('workouts.template.preview.exercises_one', { count: day.items?.length || 0 }) : t('workouts.template.preview.exercises_other', { count: day.items?.length || 0 }))}
										</div>
									</div>
									{day.isRestDay ? (
										<div className="template-preview-day-card__rest-copy">{t('workouts.template.preview.rest_day_copy')}</div>
									) : (
										<div className="template-preview-day-card__exercise-list">
											{day.items?.map((item, idx) => (
												<div key={idx} className="template-preview-day-card__exercise-item">
													<div>
														<div className="template-preview-day-card__exercise-name">{item.workoutName || item.name || item.title || item.exerciseName || item.workout?.name || item.workout?.title || t('workouts.template.exercise_item.unnamed_exercise')}</div>
														<div className="template-preview-day-card__exercise-meta">{t('workouts.template.preview.exercise_meta', { sets: item.sets || 0, reps: item.reps || 0 })}</div>
													</div>
													<div className="template-preview-day-card__exercise-index">{idx + 1}</div>
												</div>
											))}
											{(!day.items || day.items.length === 0) && (
												<div className="template-preview-day-card__empty">{t('workouts.template.day_editor.no_exercises_yet')}</div>
											)}
										</div>
									)}
								</Card>
							</Col>
						);
							})}
							{days.length === 0 && (
								<Col span={24}>
									<div className="template-preview-section__empty">{t('workouts.template.preview.no_schedule')}</div>
								</Col>
							)}
						</Row>
					</div>
				</div>
			</div>
		</>
	);
};

export default TemplatePreview;
